import React, { useState, useEffect } from "react";
import { Rating } from "@mui/material";
import "./CourseCompletionRatingPopup.css";
import api from "../../api/api";

const CourseCompletionRatingPopup = ({
  open,
  onClose,
  courseName,
  teacherName,
  courseId,
  token,
  completionStatus,
  teacher_rating,
  course_rating,
  studentId,
  ratingId,
  onRatingIdUpdate,
  teacherId,
  teacher_rating_id
}) => {
  const [courseRating, setCourseRating] = useState(0);
  const [teacherRating, setTeacherRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Sync course rating when prop arrives asynchronously
  useEffect(() => {
    if (course_rating != null) {
      setCourseRating(course_rating);
    }
  }, [course_rating]);

  // Sync teacher rating when prop arrives asynchronously
  useEffect(() => {
    if (teacher_rating != null) {
      setTeacherRating(teacher_rating);
    }
  }, [teacher_rating]);

  if (!open) return null;

  const headerContent =
    completionStatus === "50%"
      ? {
          title: "You're halfway through the course",
          subtitle: "We'd love to know how your experience has been till now.",
        }
      : {
          title: "Your opinion matters to us!",
          subtitle: "Take a moment to rate:",
        };

  const handleSubmit = async () => {
    if (!courseRating && !teacherRating) {
      onClose();
      return;
    }

    try {
      setSubmitting(true);
      const requests = [];

      // Course rating API — only if user selected a course rating
      if (courseRating) {
        const payload = {
          course: courseId,
          student: studentId,
          rating: courseRating,
        };
  
        if (ratingId) {
          requests.push(
            api.put(
              `rating/course-rating-by-student/${ratingId}/`,
              payload,
              { headers: { Authorization: `Token ${token}` } }
            )
          );
        } else {
          requests.push(
            api.post(
              `rating/course-rating-by-student/`,
              payload,
              { headers: { Authorization: `Token ${token}` } }
            ).then((res) => {
              if (res?.data?.id) {
                onRatingIdUpdate(res.data.id);
              }
            })
          );
        }
      }

      // Teacher rating API — only if user selected a teacher rating
      if (teacherRating) {

        const payload = {
          teacher: teacherId,
          student: studentId,
          rating: teacherRating,
        };

        requests.push(
          api.post(
            `rating/teacher-rating-by-student/`,
            payload,
            { headers: { Authorization: `Token ${token}` } }
          )
        );
      }

      await Promise.all(requests);
    } catch (err) {
      console.error("Rating submission failed", err);
    } finally {
      setSubmitting(false);
      onClose();
    }
  };

  const handleSkip = async () => {
    if (completionStatus === "50%") {
      try {
        const payload = {
          course: courseId,
          student: studentId,
          rating_popup_shown: true,
        };

        if (ratingId) {
          await api.put(
            `rating/course-rating-by-student/${ratingId}/`,
            payload,
            { headers: { Authorization: `Token ${token}` } }
          );
        } else {
          const res = await api.post(
            `rating/course-rating-by-student/`,
            payload,
            { headers: { Authorization: `Token ${token}` } }
          );
          if (res?.data?.id) {
            onRatingIdUpdate(res.data.id);
          }
        }
      } catch (err) {
        console.error("Skip rating_popup_shown update failed", err);
      }
    }

    onClose();
  };

  return (
    <div className="ccrp-overlay">
      <div className="ccrp-modal">

        <button className="ccrp-close-btn" onClick={handleSkip}>✕</button>

        <div className="ccrp-header">
          <h3 className="ccrp-title">{headerContent.title}</h3>
          <p className="ccrp-subtitle">{headerContent.subtitle}</p>
        </div>

        {/* Course Rating */}
        <div className="ccrp-rating-block">
          <p className="ccrp-rating-label">Course "{courseName}"</p>
          <Rating
            className="ccrp-stars"
            value={courseRating}
            onChange={(event, newValue) => setCourseRating(newValue)}
            disabled={submitting}
          />
        </div>

        {/* Teacher Rating */}
        <div className="ccrp-rating-block">
          <p className="ccrp-rating-label">Teacher "{teacherName}"</p>
          <Rating
            className="ccrp-stars"
            value={teacherRating}
            onChange={(event, newValue) => setTeacherRating(newValue)}
            disabled={submitting}
            readOnly={teacher_rating_id != null} // Disable if teacher already rated
            sx={teacher_rating_id != null ? { pointerEvents: "none", opacity: 0.5 } : {}}
          />
        </div>

        {/* Action Buttons */}
        <div className="ccrp-actions">
          <button
            className="ccrp-skip-btn"
            onClick={handleSkip}
            disabled={submitting}
          >
            Skip for now
          </button>
          <button
            className="ccrp-submit-btn"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>

      </div>
    </div>
  );
};

export default CourseCompletionRatingPopup;